/**
 * BoardPage — 게시판 모듈 메인 라우트 컴포넌트
 *
 * AppLayout > main 영역에 렌더링됨.
 * BoardContext를 소비하여 게시판/게시글을 표시.
 * (Provider는 BoardProvider에서 상위 레벨로 제공)
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useBoardContext } from '../../context/BoardContext';
import {
  resolveDisplayStyle, DisplayStyle,
  getUserViewPreference, setUserViewPreference, clearUserViewPreference,
  SWITCHABLE_STYLES, canWritePost, canManageBoard,
} from '../../models/Board';
import { markBoardRead } from '../../services/BoardService';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { My } from '../../services/My';
import PostListView from './PostListView';
import PostDetailOverlay from './PostDetailOverlay';
import BoardDashboardPage from './BoardDashboardPage';
import BoardEmptyState from './BoardEmptyState';
import BoardErrorRetry from './BoardErrorRetry';
import BoardEditModal from './BoardEditModal';
import WikiSidebar from './WikiSidebar';
import { Trans, t } from '@lingui/macro';
import { MenuToggleButton } from '../components/MenuToggleButton';
import {
  LayoutList, ChevronDown,
  ListFilter, Rows2, LayoutGrid,
  PanelLeft, Settings,
} from 'lucide-react';

/** 스타일 메타 (wiki 제외 — wiki는 유형 고정이므로 사용자 선택에 불필요).
 *  현재 사용자 뷰 전환 드롭다운은 비표시 (관리자만 변경 가능 → 별도 admin UI 예정).
 *  STYLE_META 자체는 향후 admin UI 에서 재사용을 위해 보존.
 */
const STYLE_META: { value: DisplayStyle; label: string; icon: React.ElementType }[] = [
  { value: 'notion',    label: '테이블',             icon: ListFilter },
  { value: 'basecamp',  label: '카드형 (그리드)',    icon: LayoutGrid },
  { value: 'discourse', label: '포럼형',             icon: Rows2 },
];

/** 모바일 감지 */
function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    setM(mql.matches);
    const h = (e: MediaQueryListEvent) => setM(e.matches);
    mql.addEventListener('change', h);
    return () => mql.removeEventListener('change', h);
  }, []);
  return m;
}

const BoardPage: React.FC = () => {
  const { boards, selectedBoard, selectedPost, totalPosts, loading, refreshBoards, refreshPosts, error } = useBoardContext();
  const myRole = useMyRole();
  const wsSettings = useWorkspaceSettings();
  const [showStyleMenu, setShowStyleMenu] = useState(false);
  /** 모바일/태블릿에서 위키 사이드바 드로어 열림 여부 (lg 이상은 항상 가시) */
  const [wikiSidebarOpen, setWikiSidebarOpen] = useState(false);
  const styleMenuRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // 게시글 작성 권한
  const canWrite = useMemo(() => {
    if (!selectedBoard) return false;
    return canWritePost(selectedBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids);
  }, [selectedBoard, myRole, wsSettings.board_manager_user_ids]);

  // 게시판 관리 권한 (설정 버튼 표시 조건)
  const canManageSelectedBoard = useMemo(() => {
    if (!selectedBoard) return false;
    return canManageBoard(selectedBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids);
  }, [selectedBoard, myRole, wsSettings.board_manager_user_ids]);

  // 게시판 설정 모달
  const [showBoardEdit, setShowBoardEdit] = useState(false);

  // 사용자 선호 뷰 (localStorage)
  const [userPref, setUserPref] = useState<DisplayStyle | null>(
    selectedBoard ? getUserViewPreference(selectedBoard.id) : null,
  );

  // 게시판 전환 시 사용자 선호 갱신 + 마지막 읽음 시점 기록
  useEffect(() => {
    if (selectedBoard) {
      setUserPref(getUserViewPreference(selectedBoard.id));
      // 미읽음 배지 초기화
      markBoardRead(selectedBoard.id, My.user?.id!);
    }
  }, [selectedBoard?.id]);

  const isWikiBoard = selectedBoard?.type === 'wiki';

  // 현재 적용 스타일 — 관리자(board.display_style) 우선. 사용자 선호(userPref)는 무시.
  // 사용자 view 전환 드롭다운이 비표시(관리자만 변경 가능) 정책이라, localStorage 의 stale 선호값을 무시.
  // userPref 상태/STYLE_META/handleStyleChange 등은 향후 admin UI 부활용 dead-but-preserved.
  const activeStyle = useMemo(() => {
    return resolveDisplayStyle(selectedBoard?.display_style ?? null, isMobile);
  }, [selectedBoard?.display_style, isMobile]);

  const activeStyleMeta = STYLE_META.find(s => s.value === activeStyle);

  // 뷰 전환 핸들러 — 사용자별 localStorage에 저장 (DB 변경 없음)
  const handleStyleChange = (style: DisplayStyle) => {
    if (!selectedBoard) return;
    setShowStyleMenu(false);
    setUserViewPreference(selectedBoard.id, style);
    setUserPref(style);
  };

  // 관리자 기본값으로 복원
  const handleResetToDefault = () => {
    if (!selectedBoard) return;
    setShowStyleMenu(false);
    clearUserViewPreference(selectedBoard.id);
    setUserPref(null);
  };

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (styleMenuRef.current && !styleMenuRef.current.contains(e.target as Node)) {
        setShowStyleMenu(false);
      }
    };
    if (showStyleMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showStyleMenu]);

  return (
    <>
    {/* 보드 미선택 + 게시판 있음 → 대시보드 렌더 (자체 헤더 보유) */}
    {!selectedBoard && !selectedPost && boards.length > 0 && !error ? (
      <BoardDashboardPage />
    ) : (
    <div className="flex flex-col h-full bg-background rounded-panel-mobile lg:rounded-panel overflow-hidden">
      {/* ── Header ── */}
      <header className="flex items-center gap-2.5 px-4 h-14 shrink-0 border-b border-border">
        <MenuToggleButton />

        {/* 위키 사이드바 토글 — 모바일/태블릿(lg 미만)에서만 노출 */}
        {isWikiBoard && (
          <button
            onClick={() => setWikiSidebarOpen(true)}
            className="md:hidden size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
            aria-label={t`Open page list`}
            title={t`Page list`}
          >
            <PanelLeft className="size-4" />
          </button>
        )}

        <LayoutList className="size-5 text-primary" strokeWidth={2} />

        <h1 className="text-body-lg font-semibold text-foreground truncate">
          {selectedBoard?.name ?? t`Boards`}
        </h1>

        {selectedBoard && (
          <span className="text-meta-sm text-muted-foreground tabular-nums shrink-0">
            {totalPosts}
          </span>
        )}

        {/* 뷰 전환 드롭다운 — 관리자만 변경 가능 (일단 비표시).
            STYLE_META / handleStyleChange / handleResetToDefault / showStyleMenu state /
            activeStyleMeta 등 관련 로직은 향후 admin UI 에서 재사용을 위해 컴포넌트에 보존.
            UI 자체만 렌더 안 함. */}

        {/* 게시판 설정 — 알림 설정은 좌측 메뉴(LeftMenuBoard) 의 통합 진입점에서 관리 */}
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          {/* 게시판 설정 — 관리 권한 있을 때만 표시. 권한 표시는 헤더 타이틀 옆에서 일괄. */}
          {selectedBoard && canManageSelectedBoard && (
            <button
              onClick={() => setShowBoardEdit(true)}
              className="size-8 rounded-lg flex items-center justify-center text-admin/70 hover:text-admin hover:bg-admin/10 transition-colors cursor-pointer"
              title={t`Board settings`}
            >
              <Settings className="size-4" />
            </button>
          )}
        </div>
      </header>

      {/* ── Content ── */}
      <div className="flex-1 min-h-0 relative flex">
        {/* 위키 게시판: 좌측 페이지 목록 사이드바 (상세 진입 후에도 유지) */}
        {isWikiBoard && (
          <WikiSidebar
            isOpen={wikiSidebarOpen}
            onClose={() => setWikiSidebarOpen(false)}
          />
        )}
        <div className="flex-1 min-w-0 relative">
          {selectedPost ? (
            /* 게시글 상세 — 목록을 대체하여 전체 영역에 렌더링 */
            <PostDetailOverlay />
          ) : error ? (
            <BoardErrorRetry
              message={error}
              onRetry={() => {
                if (selectedBoard) refreshPosts(selectedBoard.id);
                else refreshBoards();
              }}
            />
          ) : boards.length === 0 && !loading ? (
            <BoardEmptyState onRefresh={refreshBoards} />
          ) : (
            <PostListView displayStyle={activeStyle} showActions />
          )}
        </div>
      </div>

      {/* 게시판 설정 모달 — 삭제는 모달 내부 '위험 영역'에서 처리 */}
      {selectedBoard && showBoardEdit && (
        <BoardEditModal
          open={showBoardEdit}
          onOpenChange={setShowBoardEdit}
          board={selectedBoard}
        />
      )}
    </div>
    )}
    </>
  );
};

export default BoardPage;
